import { isPointInPolygon } from "./lat-lng-utils";
import type { Polygon, SovBuildingType } from "../ts-types/mapsTypes";
import type { IntegrationsConfig } from "../ts-types/apiTypes";
import { SOURCES } from "../constants/maps-constants";

import {
  groupBuildingsByProximity,
  groupBuildingsByRadius,
  type BuildingPolygons,
  type ProximityResult,
} from "./buildingProximityAlgorithm";
import { convertStructuresToBuildingPolygons } from "./wktParser";

export const FIRE_PML_DEFAULT_MIN_DISTANCE_FEET = 100;

export const FIRE_PML_DEFAULT_RADIUS_MILES = 10;

export const FIRE_PML_GROUPING_METHODS = {
  MIN_DISTANCE: "minDistance",
  RADIUS: "radius",
} as const;

export type FirePMLGroupingMethod =
  (typeof FIRE_PML_GROUPING_METHODS)[keyof typeof FIRE_PML_GROUPING_METHODS];


export const FIRE_PML_GROUP_COLORS = [
  "#145DF0",
  "#2ECC71",
  "#F59E0B",
  "#8B5CF6",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#84CC16",
  "#F97316",
  "#14B8A6",
];

export type FirePMLGroupSummary = {
  groupIndex: number;
  label: string;
  color: string;
  structureIds: string[];
  structureCount: number;
  matchedBuildingCount: number;
  totalTIV: number;
  totalArea: number;
  areaPercentage: number;
};

export type FirePMLGroupingData = {
  proximityResult: ProximityResult;
  groupSummaries: FirePMLGroupSummary[];
  buildingGroupIndexByUid: Map<string, number>;
  structureGroupIndexByStructureIndex: Map<number, number>;
  totalArea: number;
  totalTIV: number;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const hasValidCoordinates = (building: SovBuildingType) =>
  isFiniteNumber(building.lat) && isFiniteNumber(building.lng);

const isPointInsidePolygonGeometry = (
  point: { lat: number; lng: number },
  polygon?: Polygon,
) => {
  if (!polygon?.outer?.length) return false;
  if (!isPointInPolygon(point, polygon.outer)) return false;

  return !polygon.holes?.some(
    (hole) => hole.length > 0 && isPointInPolygon(point, hole),
  );
};

const parseStructureIndex = (structureId: string) => {
  const parsedIndex = Number.parseInt(structureId.split("_")[1] ?? "", 10);
  return Number.isNaN(parsedIndex) ? null : parsedIndex;
};

const normalizeNonNegativeInt = (
  value: number | string | null | undefined,
  fallback: number,
) => {
  const parsed =
    typeof value === "string"
      ? Number.parseFloat(value.replace(/,/g, ""))
      : value;

  return isFiniteNumber(parsed) ? Math.max(0, Math.round(parsed)) : fallback;
};

export const getFirePMLPermission = ({
  canAccessDeveloperTools,
  integrationSettings,
}: {
  canAccessDeveloperTools: boolean;
  integrationSettings: IntegrationsConfig;
}) => canAccessDeveloperTools || !!integrationSettings[SOURCES.PING_HAZARD];

export const normalizeFirePMLMinDistanceFeet = (
  value: number | string | null | undefined,
  fallback: number = FIRE_PML_DEFAULT_MIN_DISTANCE_FEET,
) => normalizeNonNegativeInt(value, fallback);

export const normalizeFirePMLRadiusMiles = (
  value: number | string | null | undefined,
  fallback: number = FIRE_PML_DEFAULT_RADIUS_MILES,
) => normalizeNonNegativeInt(value, fallback);

const buildFirePMLGroupingData = (
  proximityResult: ProximityResult,
  resolveGroup: (group: string[]) => {
    buildings: SovBuildingType[];
    structureIndices: number[];
  },
): FirePMLGroupingData => {
  const buildingGroupIndexByUid = new Map<string, number>();
  const structureGroupIndexByStructureIndex = new Map<number, number>();

  const baseGroupSummaries = proximityResult.groups.map((group, groupIndex) => {
    const { buildings, structureIndices } = resolveGroup(group);

    buildings.forEach((building) =>
      buildingGroupIndexByUid.set(building.uid, groupIndex),
    );
    structureIndices.forEach((structureIndex) =>
      structureGroupIndexByStructureIndex.set(structureIndex, groupIndex),
    );

    return {
      groupIndex,
      label: `Group ${groupIndex + 1}`,
      color: FIRE_PML_GROUP_COLORS[groupIndex % FIRE_PML_GROUP_COLORS.length],
      structureIds: group,
      structureCount: group.length,
      matchedBuildingCount: buildings.length,
      totalTIV: buildings.reduce(
        (total, building) => total + (building.limits_total_limit ?? 0),
        0,
      ),
      totalArea: buildings.reduce(
        (total, building) => total + (building.const_bldg_area ?? 0),
        0,
      ),
      areaPercentage: 0,
    } satisfies FirePMLGroupSummary;
  });

  const totalArea = baseGroupSummaries.reduce(
    (total, groupSummary) => total + groupSummary.totalArea,
    0,
  );
  const totalTIV = baseGroupSummaries.reduce(
    (total, groupSummary) => total + groupSummary.totalTIV,
    0,
  );

  const groupSummaries = baseGroupSummaries.map((groupSummary) => ({
    ...groupSummary,
    areaPercentage:
      totalArea > 0 ? (groupSummary.totalArea / totalArea) * 100 : 0,
  }));

  return {
    proximityResult,
    groupSummaries,
    buildingGroupIndexByUid,
    structureGroupIndexByStructureIndex,
    totalArea,
    totalTIV,
  };
};

const groupSovBuildingsByRadius = (
  buildings: SovBuildingType[] | null | undefined,
  radiusMiles: number,
): FirePMLGroupingData | null => {
  const validBuildings = (buildings ?? []).filter(hasValidCoordinates);
  if (validBuildings.length === 0) {
    return null;
  }

  const buildingsByUid = new Map(
    validBuildings.map((building) => [building.uid, building]),
  );

  const pointPolygons: BuildingPolygons = {};
  validBuildings.forEach((building) => {
    pointPolygons[building.uid] = [[building.lat, building.lng]];
  });

  const proximityResult = groupBuildingsByRadius(pointPolygons, radiusMiles);

  return buildFirePMLGroupingData(proximityResult, (group) => ({
    buildings: group
      .map((uid) => buildingsByUid.get(uid))
      .filter((building): building is SovBuildingType => building !== undefined),
    structureIndices: [],
  }));
};

export const getFirePMLGroupingData = ({
  structures,
  buildings,
  thresholdFeet = FIRE_PML_DEFAULT_MIN_DISTANCE_FEET,
  radiusMiles = FIRE_PML_DEFAULT_RADIUS_MILES,
  method = FIRE_PML_GROUPING_METHODS.MIN_DISTANCE,
}: {
  structures?: Polygon[] | null;
  buildings?: SovBuildingType[] | null;
  thresholdFeet?: number;
  radiusMiles?: number;
  method?: FirePMLGroupingMethod;
}): FirePMLGroupingData | null => {
  if (method === FIRE_PML_GROUPING_METHODS.RADIUS) {
    return groupSovBuildingsByRadius(
      buildings,
      normalizeFirePMLRadiusMiles(radiusMiles),
    );
  }

  if (!structures?.length) {
    return null;
  }

  const buildingPolygons = convertStructuresToBuildingPolygons(structures);

  if (Object.keys(buildingPolygons).length === 0) {
    return null;
  }

  const proximityResult = groupBuildingsByProximity(
    buildingPolygons,
    normalizeFirePMLMinDistanceFeet(thresholdFeet),
  );


  const buildingsByStructureIndex = new Map<number, SovBuildingType[]>();

  buildings
    ?.filter(hasValidCoordinates)
    .forEach((building) => {
      const structureIndex = structures.findIndex((structure) =>
        isPointInsidePolygonGeometry(
          {
            lat: building.lat,
            lng: building.lng,
          },
          structure,
        ),
      );

      if (structureIndex < 0) {
        return;
      }

      const matchedBuildings =
        buildingsByStructureIndex.get(structureIndex) ?? [];
      matchedBuildings.push(building);
      buildingsByStructureIndex.set(structureIndex, matchedBuildings);
    });

  return buildFirePMLGroupingData(proximityResult, (group) => {
    const structureIndices = group
      .map(parseStructureIndex)
      .filter(
        (structureIndex): structureIndex is number => structureIndex !== null,
      );

    return {
      buildings: structureIndices.flatMap(
        (structureIndex) => buildingsByStructureIndex.get(structureIndex) ?? [],
      ),
      structureIndices,
    };
  });
};
